import Dexie, { Table } from "dexie";
import logger from "dexie-logger";
import { NULL_UUID } from "@iterate/common/types/basicTypes";
import {
  EventRecord,
  GoalRecord,
  HabitRecord,
  InstanceOverrideRecord,
  JournalRecord,
  MetricRecord,
  MetricSeriesRecord,
  TaskRecord,
} from "./types";

type SerialisableBigInt = bigint & {
  toJSON(): string;
};

(BigInt.prototype as SerialisableBigInt).toJSON = function () {
  return this.toString();
};

interface SettingsRecord {
  id: string;
  syncId: bigint;
}

export interface DbTables {
  metricSeries: Table<MetricSeriesRecord>;
  metrics: Table<MetricRecord>;
  goals: Table<GoalRecord>;
  journals: Table<JournalRecord>;
  tasks: Table<TaskRecord>;
  habits: Table<HabitRecord>;
  instances: Table<InstanceOverrideRecord>;
  eventLog: Table<EventRecord>;
  settings: Table<SettingsRecord>;
}

// Define Dexie database class
export class Database extends Dexie implements DbTables {
  metricSeries!: Table<MetricSeriesRecord>;
  metrics!: Table<MetricRecord>;
  goals!: Table<GoalRecord>;
  journals!: Table<JournalRecord>;
  tasks!: Table<TaskRecord>;
  habits!: Table<HabitRecord>;
  instances!: Table<InstanceOverrideRecord>;
  eventLog!: Table<EventRecord>;
  settings!: Table<SettingsRecord>;

  constructor() {
    super("Database");

    if (process.env.NODE_ENV === "development") {
      Dexie.debug = true;
      this.use(logger());
    }

    this.version(1).stores({
      metrics: "&id, [label+date], label",
      goals: "&id",
      journals: "&id, date",
      tasks: "&id, completed, parentId",
      habits: "&id, goalId, parentId, order",
      instances: "&id, date, goalId, templateId, parentId, &[templateId+date]",
      eventLog: "++id",
      settings: "&id",
    });

    this.version(2).stores({
      metricSeries: "&id, label",
      metrics: "&id, [seriesId+date], seriesId",
      goals: "&id",
      journals: "&id, date",
      tasks: "&id, completed, parentId",
      habits: "&id, goalId, parentId, order",
      instances: "&id, date, goalId, templateId, parentId, &[templateId+date]",
      eventLog: "++id",
      settings: "&id",
    });

    this.version(4)
      .stores({
        metricSeries: "&id, label",
        metrics: "&id, [seriesId+date], seriesId",
        goals: "&id",
        journals: "&id, date",
        tasks: "&id, completed, parentId",
        habits: "&id, goalId, parentId, order",
        instances: "&id, date, goalId, templateId, parentId, &[templateId+date]",
        eventLog: "++id",
        settings: "&id",
      })
      .upgrade(async (tx) => {
        const addComponents = (item: TaskRecord | HabitRecord | InstanceOverrideRecord) => {
          item.components = [];
          if (item.startTime) {
            item.components.push("timebox");
          }
          if (item.condition) {
            item.components.push("condition");
          }
          if ("goalId" in item && item.goalId) {
            item.components.push("goals");
          }
        };
        await tx
          .table("tasks")
          .toCollection()
          .modify((task) => {
            addComponents(task);
          });
        await tx
          .table("habits")
          .toCollection()
          .modify((habit) => {
            addComponents(habit);
          });
        await tx
          .table("instances")
          .toCollection()
          .modify((instance) => {
            addComponents(instance);
          });
      });

    this.version(5)
      .stores({
        metricSeries: "&id, label",
        metrics: "&id, [seriesId+date], seriesId",
        goals: "&id",
        journals: "&id, date",
        tasks: "&id, folders, completed, parentId",
        habits: "&id, goalId, parentId, order",
        instances: "&id, date, goalId, templateId, parentId, &[templateId+date]",
        eventLog: "++id",
        settings: "&id",
      })
      .upgrade(async (tx) => {
        await tx
          .table("tasks")
          .toCollection()
          .modify((task: TaskRecord) => {
            task.folders = task.folders ?? [];
          });
      });

    this.version(6)
      .stores({
        metricSeries: "&id, label",
        metrics: "&id, [seriesId+date], seriesId",
        goals: "&id",
        journals: "&id, date",
        tasks: "&id, folders, goalId, completed, parentId",
        habits: "&id, goalId, parentId, order",
        instances: "&id, date, goalId, templateId, parentId, &[templateId+date]",
        eventLog: "++id",
        settings: "&id",
      })
      .upgrade(async (tx) => {
        await tx
          .table("tasks")
          .toCollection()
          .modify((task: TaskRecord) => {
            if (task.goalId === undefined) {
              task.goalId = NULL_UUID;
            }
            if (task.goalOrder === undefined) {
              task.goalOrder = null;
            }
          });
      });
  }

  update<K extends keyof Database>(
    tableName: K,
    id: string,
    values: Partial<Database[K] extends Table<infer U> ? U : never>,
  ) {
    return this.table(tableName).update(id, values);
  }
}

// Export a singleton instance
export const db = new Database();
